import React from "react";
import { Link } from "react-router-dom";
import { IconButton, SvgIcon, Tooltip } from "@mui/material";
import type { IconButtonProps } from "@mui/material/IconButton";
import type { TooltipProps } from "@mui/material/Tooltip";
import { DASHBOARD_NAVY } from "./dashboardUi";

export const RETREAT_ACTION_ICON_SIZE = 17;
export const RETREAT_ACTION_BUTTON_SIZE = 34;
export const RETREAT_ACTION_ROW_SPACING = 0.75;

const retreatActionIconSx = {
  fontSize: RETREAT_ACTION_ICON_SIZE,
};

export const retreatActionButtonSx = {
  width: RETREAT_ACTION_BUTTON_SIZE,
  height: RETREAT_ACTION_BUTTON_SIZE,
  p: "8.5px",
  borderRadius: "50%",
  "&:hover": {
    bgcolor: "rgba(0, 0, 0, 0.04)",
  },
};

function EditOutlinedIcon(props: React.ComponentProps<typeof SvgIcon>) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="m14.06 9.02.92.92L5.92 19H5v-.92zM17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" />
    </SvgIcon>
  );
}

function DeleteForeverOutlinedIcon(props: React.ComponentProps<typeof SvgIcon>) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M15.5 4l-1-1h-5l-1 1H5v2h14V4zM6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM8 9h8v10H8V9z" />
    </SvgIcon>
  );
}

type ActionIconButtonProps = {
  title: string;
  ariaLabel: string;
  tooltipPlacement?: TooltipProps["placement"];
  iconButtonProps: IconButtonProps;
};

function ActionIconButton({
  title,
  ariaLabel,
  tooltipPlacement = "bottom",
  iconButtonProps,
}: ActionIconButtonProps) {
  const { disabled, sx, ...rest } = iconButtonProps;
  const button = (
    <IconButton
      size="small"
      aria-label={ariaLabel}
      disabled={disabled}
      sx={{ ...retreatActionButtonSx, ...sx }}
      {...rest}
    />
  );

  return (
    <Tooltip title={title} placement={tooltipPlacement} arrow>
      {disabled ? <span>{button}</span> : button}
    </Tooltip>
  );
}

type RetreatEditIconButtonProps = {
  title: string;
  ariaLabel: string;
  tooltipPlacement?: TooltipProps["placement"];
  to?: string;
} & Omit<IconButtonProps, "children" | "component" | "href">;

export function RetreatEditIconButton({
  title,
  ariaLabel,
  tooltipPlacement,
  to,
  disabled,
  sx,
  ...iconButtonProps
}: RetreatEditIconButtonProps) {
  const linkProps = to ? { component: Link, to } : {};

  return (
    <ActionIconButton
      title={title}
      ariaLabel={ariaLabel}
      tooltipPlacement={tooltipPlacement}
      iconButtonProps={{
        ...iconButtonProps,
        ...linkProps,
        disabled,
        sx: {
          color: DASHBOARD_NAVY,
          ...sx,
        },
        children: <EditOutlinedIcon sx={retreatActionIconSx} />,
      }}
    />
  );
}

export function RetreatDeleteIconButton({
  title = "Delete retreat record",
  ariaLabel,
  tooltipPlacement,
  sx,
  ...iconButtonProps
}: Omit<ActionIconButtonProps, "iconButtonProps" | "title"> &
  Partial<Pick<ActionIconButtonProps, "title">> &
  Omit<IconButtonProps, "children">) {
  return (
    <ActionIconButton
      title={title}
      ariaLabel={ariaLabel}
      tooltipPlacement={tooltipPlacement}
      iconButtonProps={{
        ...iconButtonProps,
        sx: {
          color: "error.main",
          ...sx,
        },
        children: <DeleteForeverOutlinedIcon sx={retreatActionIconSx} />,
      }}
    />
  );
}
